import axios, { AxiosError } from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CarrierCapacityShipmentMatchDto } from "models/dto/CarrierCapacityShipmentMatch";
import { CarrierCapacityInfoDto } from "models/dto/CarrierCapacityInfoDto";

export type CapacityMatchStatus = {
  isLoading: boolean;
  matches: CarrierCapacityShipmentMatchDto[];
};

export type SortedCapacityIds = {
  matches: number[];
  noMatches: number[];
  pending: number[];
};

export const useGetCarrierCapacityShipmentMatches = <
  T extends "Capacity" | "Preferred Lane",
>({
  carrierId,
  capacities,
}: {
  carrierId: number;
  capacities: CarrierCapacityInfoDto<T>[];
}) => {
  const [capacityMatchMap, setCapacityMatchMap] = useState<
    Record<number, CapacityMatchStatus>
  >({});

  const fetchShipmentMatches = useCallback(
    async (signal: AbortSignal) => {
      setCapacityMatchMap(
        capacities.reduce<Record<number, CapacityMatchStatus>>(
          (acc, capacity) => {
            if (capacity.id) {
              acc[capacity.id] = { isLoading: true, matches: [] };
            }
            return acc;
          },
          {}
        )
      );

      const limit = 3;
      const taskQueue: Promise<void>[] = [];

      const limitPromises = async (task: () => Promise<void>) => {
        if (taskQueue.length >= limit) {
          await Promise.race(taskQueue);
        }

        const promise = task().finally(() => {
          taskQueue.splice(taskQueue.indexOf(promise), 1);
        });

        taskQueue.push(promise);
      };

      for (const capacity of capacities) {
        const capacityId = capacity.id; // TS is not picking up that this will NOT be null below without reassignment ???

        if (!capacityId) continue;

        const fetcher = async () => {
          if (signal.aborted) {
            return;
          }

          try {
            const result = await axios.get<CarrierCapacityShipmentMatchDto[]>(
              `/api/carriers/${carrierId}/capacities/${capacityId}/shipmentMatches`,
              { signal }
            );

            setCapacityMatchMap((previousCapacityMatchMap) => ({
              ...previousCapacityMatchMap,
              [capacityId]: { isLoading: false, matches: result?.data ?? [] }, // If the request is aborted, result is null
            }));
          } catch (error) {
            console.error((error as AxiosError).message);

            setCapacityMatchMap((previousCapacityMatchMap) => ({
              ...previousCapacityMatchMap,
              [capacityId]: { isLoading: false, matches: [] },
            }));
          }
        };

        await limitPromises(fetcher);
      }

      await Promise.all(taskQueue);
    },
    [carrierId, capacities, setCapacityMatchMap]
  );

  useEffect(() => {
    const abortController = new AbortController();

    fetchShipmentMatches(abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [fetchShipmentMatches]);

  const sortedCapacityIds = useMemo(
    () =>
      Object.entries(capacityMatchMap).reduce<SortedCapacityIds>(
        (acc, [capacityId, { isLoading, matches }]) => {
          if (isLoading) {
            acc.pending.push(Number(capacityId));
          } else if (matches.length > 0) {
            acc.matches.push(Number(capacityId));
          } else {
            acc.noMatches.push(Number(capacityId));
          }

          return acc;
        },
        { matches: [], noMatches: [], pending: [] }
      ),
    [capacityMatchMap]
  );

  const totalCapacityShipments = useMemo(
    () =>
      Object.values(capacityMatchMap).reduce<Set<number>>(
        (acc, { matches }) => {
          matches.forEach((match) => {
            acc.add(match.tmsShipmentId);
          });
          return acc;
        },
        new Set()
      ).size,
    [capacityMatchMap]
  );

  return {
    capacityMatchMap,
    sortedCapacityIds,
    totalCapacityShipments,
  };
};
